import React from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { instanceIdFromPathname } from '../../../utils/companyLogo';
import { clearOmdProductClientCache } from '../../stammdaten/utils/omdProductCache';
import { OmdAddConnectionModal, type OmdSupplierOption } from '../components/OmdAddConnectionModal';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsStatusChip } from '../components/SettingsStatusChip';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';

export function OmdSettingsSection() {
  const location = useLocation();
  const instanceId = React.useMemo(() => instanceIdFromPathname(location.pathname), [location.pathname]);

  const [suppliers, setSuppliers] = React.useState<OmdSupplierOption[]>([]);
  const [canManage, setCanManage] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [headerNotice, setHeaderNotice] = React.useState<string | null>(null);
  const [rowNotice, setRowNotice] = React.useState<{ id: string; text: string } | null>(null);
  const [testingSlug, setTestingSlug] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editSlug, setEditSlug] = React.useState<string | null>(null);

  const load = React.useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/omd', { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Open Master Data konnte nicht geladen werden.');
      setSuppliers(Array.isArray(d.suppliers) ? (d.suppliers as OmdSupplierOption[]) : []);
      setCanManage(Boolean(d.canManage));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const configured = suppliers.filter((s) => s.configured);

  const testConnection = async (slug: string) => {
    setTestingSlug(slug);
    setError(null);
    setHeaderNotice(null);
    setRowNotice(null);
    try {
      const res = await fetch(`/api/settings/omd/${encodeURIComponent(slug)}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Prüfung fehlgeschlagen.');
      setRowNotice({ id: slug, text: 'Geprüft.' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTestingSlug(null);
    }
  };

  const removeCredentials = async (slug: string) => {
    if (!window.confirm('Katalog-Zugang wirklich entfernen?')) return;
    setError(null);
    setHeaderNotice(null);
    setRowNotice(null);
    try {
      const res = await fetch(`/api/settings/omd/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Entfernen fehlgeschlagen.');
      clearOmdProductClientCache(instanceId);
      await load({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <SettingsCard
        title="Zugänge"
        actions={
          <>
            <SettingsCardSavedStatus saved={Boolean(headerNotice)}>{headerNotice}</SettingsCardSavedStatus>
            {canManage && !loading ? (
              <button
                type="button"
                className={SETTINGS_BTN_PRIMARY}
                onClick={() => {
                  setEditSlug(null);
                  setModalOpen(true);
                }}
              >
                <Plus className="w-4 h-4" strokeWidth={1.75} />
                Hinzufügen
              </button>
            ) : null}
          </>
        }
      >
        <div className="space-y-5">
          {loading ? (
            <InlineLoadingState label="Einstellungen werden geladen …" className="justify-start" />
          ) : (
            <>
              {error ? (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
              ) : null}
              {!canManage ? (
                <p className="text-sm text-app-text-subtle">Nur Inhaber oder Administratoren können Zugänge anlegen.</p>
              ) : null}
              {suppliers.length === 0 ? (
                <p className="text-sm text-app-text-subtle">Keine Großhändler mit Open Master Data verfügbar.</p>
              ) : (
                <div className="space-y-3">
                  <h3 className={SETTINGS_FIELD_LABEL}>Gespeicherte Zugänge</h3>
                  {configured.length === 0 ? (
                    <p className="text-sm text-app-text-subtle">Noch kein Katalog-Zugang.</p>
                  ) : (
                    configured.map((s) => (
                      <div key={s.slug} className="rounded-md border border-app-border px-3 py-2">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <div className="min-w-0 sm:flex-1">
                            <p className="text-sm font-medium text-app-text leading-5 truncate">{s.name}</p>
                          </div>
                          <div className="flex items-center justify-between gap-3 sm:justify-end">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <SettingsStatusChip tone="ok">Katalog</SettingsStatusChip>
                            </div>
                            {canManage ? (
                              <div className="flex items-center gap-2 shrink-0">
                                {rowNotice?.id === s.slug ? (
                                  <span className="text-sm text-emerald-600 font-medium">{rowNotice.text}</span>
                                ) : null}
                                <button
                                  type="button"
                                  className={SETTINGS_BTN_OUTLINE}
                                  onClick={() => void testConnection(s.slug)}
                                  disabled={testingSlug === s.slug}
                                >
                                  {testingSlug === s.slug ? <LoadingSpinner size="sm" /> : null}
                                  Prüfen
                                </button>
                                <button
                                  type="button"
                                  className={`${SETTINGS_BTN_OUTLINE} !px-2`}
                                  onClick={() => {
                                    setEditSlug(s.slug);
                                    setModalOpen(true);
                                  }}
                                  title="Zugang bearbeiten"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  className={`${SETTINGS_BTN_DANGER_OUTLINE} !px-2`}
                                  onClick={() => void removeCredentials(s.slug)}
                                  title="Zugang entfernen"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SettingsCard>

      <OmdAddConnectionModal
        open={modalOpen}
        suppliers={suppliers}
        editSlug={editSlug}
        onClose={() => setModalOpen(false)}
        onSaved={async (result) => {
          setModalOpen(false);
          clearOmdProductClientCache(instanceId);
          await load({ silent: true });
          if (result.testedOk) {
            setError(null);
            setRowNotice(null);
            setHeaderNotice('Gespeichert.');
          } else {
            setHeaderNotice(null);
            setError(result.testError || 'Speichern fehlgeschlagen.');
          }
        }}
      />
    </>
  );
}
